import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Plus, Megaphone, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AnnouncementManagement() {
  const [announcements, setAnnouncements] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', scope: 'school', target_division: '', target_class_id: '', target_roles: [] });

  const load = () => api.get('/admin/announcements').then(r => setAnnouncements(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    try {
      await api.post('/admin/announcements', form);
      toast.success('Announcement published');
      setShowDialog(false);
      setForm({ title: '', content: '', scope: 'school', target_division: '', target_class_id: '', target_roles: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const scopeLabels = { school: 'School-wide', division: 'Division', class: 'Class' };

  return (
    <div data-testid="announcement-management">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-[#1B2A4A] flex items-center gap-2"><Megaphone className="w-6 h-6" /> Announcements</h1>
        <Button onClick={() => setShowDialog(true)} className="bg-[#1B2A4A]" data-testid="create-announcement-btn"><Plus className="w-4 h-4 mr-1" /> New Announcement</Button>
      </div>
      <div className="space-y-4">
        {announcements.length === 0 ? (
          <Card><CardContent className="py-8 text-center text-slate-400">No announcements yet</CardContent></Card>
        ) : announcements.map(a => (
          <Card key={a.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg text-[#1B2A4A]">{a.title}</CardTitle>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">{scopeLabels[a.scope] || a.scope}</Badge>
                    <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleDateString()}</span>
                    <span className="text-xs text-slate-400">by {a.author_name || 'Admin'}</span>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={async () => { if(window.confirm('Delete?')){ await api.delete(`/admin/announcements/${a.id}`); toast.success('Deleted'); load(); }}}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </CardHeader>
            <CardContent><p className="text-sm text-slate-600 whitespace-pre-wrap">{a.content}</p></CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Announcement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="announcement-title-input" /></div>
            <div><Label>Content</Label><Textarea rows={4} value={form.content} onChange={e => setForm({...form, content: e.target.value})} data-testid="announcement-content-input" /></div>
            <div><Label>Scope</Label><Select value={form.scope} onValueChange={v => setForm({...form, scope: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="school">School-wide</SelectItem><SelectItem value="division">Division</SelectItem><SelectItem value="class">Class</SelectItem></SelectContent></Select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button><Button onClick={handleSave} className="bg-[#1B2A4A]" data-testid="publish-announcement-btn">Publish</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
